import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { convertDatetimeLocalToUTC } from '@/utils/timezoneMath';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
// Core-100 R4 X4 (CORE-AE D/S): the modal writes through the governed server path, never a browser UPDATE.
import { editTicketDetails } from '@/lib/tickets/governedTicketEdits';
import { gateDueOutCompanionField } from '@/utils/dueOutEditAuthorization';
import { getMinutesInShopTZ } from '@/utils/timezoneMath';
import { getShopDayLabel } from '@/utils/shopTime';
import { clock } from '@/utils/clock';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
// Phase 5.2 — Due Out edits require authorized role + reason + audit context.
// `canEditDueOut` is the role gate; `DUE_OUT_EDIT_REASONS` populates the
// Reason dropdown; `requiresDueOutEditReason` decides when to show it.
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import {
  canEditDueOut,
  requiresDueOutEditReason,
  DUE_OUT_EDIT_REASONS,
  type DueOutEditReason,
} from '@/utils/dueOutEditAuthorization';

interface EditTicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  initialTicketType: 'Waiter' | 'Drop-off';
  initialScheduledStartTime?: string | null;
  initialDueOut?: string | null;
  initialIsComeback: boolean;
  predictedMinutes: number;
}

export const EditTicketDetailsModal = ({ 
  isOpen, 
  onClose, 
  ticketId,
  initialTicketType,
  initialScheduledStartTime = null,
  initialDueOut = null,
  initialIsComeback = false,
  predictedMinutes,
}: EditTicketDetailsModalProps) => {
  const [ticketType, setTicketType] = useState<'Waiter' | 'Drop-off'>(initialTicketType);
  const [scheduledStartTime, setScheduledStartTime] = useState('');
  const [dueOut, setDueOut] = useState('');
  const [isComeback, setIsComeback] = useState(initialIsComeback);
  const [isSaving, setIsSaving] = useState(false);
  // Phase 5.2 — Due Out edit captures a reason + optional reason text +
  // optional customer-communication acknowledgement. UI shows the block
  // only when the Due Out field actually differs from `initialDueOut`.
  const [dueOutReason, setDueOutReason] = useState<DueOutEditReason | ''>('');
  const [dueOutReasonText, setDueOutReasonText] = useState<string>('');
  const [dueOutCustomerNotified, setDueOutCustomerNotified] = useState<boolean>(false);
  const { shopTimezone } = useShopTimezone();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { role } = usePermissions();

  // Format datetime-local input value from UTC ISO string to shop timezone
  const formatDatetimeLocal = (isoString: string | null) => {
    if (!isoString) return '';
    try {
      const utcDate = new Date(isoString);
      const zonedDate = toZonedTime(utcDate, shopTimezone);
      return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTicketType(initialTicketType);
      setScheduledStartTime(formatDatetimeLocal(initialScheduledStartTime));
      setDueOut(formatDatetimeLocal(initialDueOut));
      setIsComeback(initialIsComeback);
      // Phase 5.2 — reset Due Out edit context fields each open.
      setDueOutReason('');
      setDueOutReasonText('');
      setDueOutCustomerNotified(false);
    }
  }, [isOpen, initialTicketType, initialScheduledStartTime, initialDueOut, initialIsComeback]);

  // Phase 5.2 — proposed new due_out as a UTC ISO (or null when cleared).
  // Drives both the audit context and the on-screen Reason block.
  const proposedNewDueOutIso: string | null = dueOut
    ? (() => {
        try {
          return convertDatetimeLocalToUTC(dueOut, shopTimezone);
        } catch {
          return null;
        }
      })()
    : null;
  const dueOutWillChange = requiresDueOutEditReason(
    initialDueOut ?? null,
    proposedNewDueOutIso,
  );
  const dueOutAuthorized = canEditDueOut(role);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Validate: if both scheduled_start_time and due_out are set, ensure finish is before due out
      if (scheduledStartTime && dueOut) {
        const start = new Date(scheduledStartTime);
        const due = new Date(dueOut);
        const predictedFinish = new Date(start.getTime() + predictedMinutes * 60000);
        
        if (predictedFinish > due) {
          toast({
            title: 'Validation Error',
            description: `Predicted finish time (${predictedFinish.toLocaleTimeString()}) would exceed promised due out time (${due.toLocaleTimeString()})`,
            variant: 'destructive',
          });
          setIsSaving(false);
          return;
        }
      }

    const updates: any = {
      type: ticketType,
      is_comeback: isComeback,
    };

      // If switching FROM Waiter TO Drop-off, clear scheduled_start_time.
      // Chronomancer R-WAITER-2: do NOT null start_time — `tickets.start_time`
      // is NOT NULL (verified against generated types.ts — `start_time: string`
      // with no null union). Previously `updates.start_time = null` caused
      // the conversion UPDATE to fail the NOT-NULL constraint, surfacing as
      // a generic "Failed to update ticket" toast while the advisor believed
      // the conversion succeeded. The Drop-off semantically inherits the
      // current start_time as its initial cascade anchor — leave it as-is;
      // global cascade will reflow the ticket now that scheduled_start_time
      // (the lock) is cleared.
      if (initialTicketType === 'Waiter' && ticketType === 'Drop-off') {
        updates.scheduled_start_time = null;
      } else if (ticketType === 'Waiter' && scheduledStartTime) {
        // If Waiter and scheduled start time is set, sync start_time with it
        const scheduledISO = convertDatetimeLocalToUTC(scheduledStartTime, shopTimezone);
        updates.scheduled_start_time = scheduledISO;
        updates.start_time = scheduledISO;
      }

      if (dueOut) {
        updates.due_out = convertDatetimeLocalToUTC(dueOut, shopTimezone);
      } else {
        updates.due_out = null;
      }

      // V-A-5: route the mixed payload through the canonical helper. Schedule
      // fields (`scheduled_start_time`, `start_time`) flow through the
      // allowlisted `updates` arg; non-schedule fields (`type`, `is_comeback`,
      // `due_out`) flow through `companionFields` so the row write stays
      // atomic. `skipCascade: true` PRESERVES the existing R-WAITER-8 cascade
      // block below — without it, the helper would auto-cascade on every save
      // (including is_comeback-only toggles that the original code
      // intentionally did NOT cascade for).
      const SCHEDULE_KEYS_FOR_HELPER = ['scheduled_start_time', 'start_time'] as const;
      const scheduleUpdates: Record<string, unknown> = {};
      const companionFields: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(updates)) {
        if ((SCHEDULE_KEYS_FOR_HELPER as readonly string[]).includes(k)) {
          scheduleUpdates[k] = v;
        } else {
          companionFields[k] = v;
        }
      }

      // Phase 5.2 — when Due Out changes, supply the audit context. The
      // helper runs `gateDueOutCompanionField` (pure) and fails closed
      // if role/reason/actor are missing or invalid. Bucket 4.5 §4.5.7.
      const dueOutEditAuditContext = dueOutWillChange
        ? {
            oldDueOut: initialDueOut ?? null,
            actorUserId: user?.id ?? '',
            role: role ?? '',
            reason: (dueOutReason || 'other') as DueOutEditReason,
            reasonText: dueOutReasonText || null,
            customerCommunicationApproved: dueOutCustomerNotified || null,
          }
        : undefined;

      // ROLLOVER CHAIN (Waiter time on a chain row). The picked time is
      // scheduling INTENT, never a direct start_time write on one row: that
      // would detach it from the chain and leave the rest of the chain stale.
      // The rollover chain authority re-plans this row and every unresolved
      // row after it FROM the promised time, which must hold exactly (Waiter
      // hard time) — a time the chain cannot start at (off hours, a break, a
      // day off, before the part it follows ends) is refused, and so is a part
      // that has already started (history). All or nothing; the Waiter lock
      // (scheduled_start_time) and the other details land only after the
      // chain has moved, and the Due Out gate is checked BEFORE it moves.
      if (ticketType === 'Waiter' && scheduleUpdates.start_time) {
        const { isRolloverChainRow, replanRolloverChainFromRow } = await import('@/utils/rolloverChain');
        if (await isRolloverChainRow(ticketId)) {
          const gate = gateDueOutCompanionField({
            companionFields,
            auditContext: dueOutEditAuditContext ?? null,
            ticketId,
            now: clock.now(),
          });
          if (!gate.ok) throw new Error(`Due Out edit rejected: ${gate.errors.join(', ')}`);
          const promisedIso = String(scheduleUpdates.start_time);
          const replan = await replanRolloverChainFromRow(
            ticketId,
            { date: getShopDayLabel(promisedIso, shopTimezone), min: getMinutesInShopTZ(promisedIso, shopTimezone) },
            { mayPullEarlier: true, requireStartAtFloor: true, label: 'edit_ticket_details_modal.waiter' },
          );
          if (!replan || replan.status === 'refused' || replan.status === 'conflict') {
            const why = replan?.status === 'refused'
              ? (replan.reason === 'row is history' ? 'this part has already started — its time is history' : replan.reason)
              : 'the chain changed while re-planning — try again';
            throw new Error(`This Waiter time cannot be set on a rollover part: ${why}`);
          }
          // The chain authority placed the row; only the Waiter lock remains to write.
          delete scheduleUpdates.start_time;
        }
      }

      // Core-100 R4 X4 (CORE-AE D/S): ONE governed server write (edit_ticket_details) — front office only, the
      // allow-listed fields only, and a Due Out change carries its reason; the server reads the OLD Due Out itself
      // and records the change (audit_log). The browser gate below still runs first so the operator is told before
      // anything is sent; the server re-decides.
      const dueOutGate = gateDueOutCompanionField({
        companionFields,
        auditContext: dueOutEditAuditContext ?? null,
        ticketId,
        now: clock.now(),
      });
      if (!dueOutGate.ok) throw new Error(`Due Out edit rejected: ${dueOutGate.errors.join(', ')}`);
      const result = await editTicketDetails(
        ticketId,
        { ...scheduleUpdates, ...companionFields },
        dueOutEditAuditContext
          ? {
              reason: dueOutEditAuditContext.reason,
              reason_text: dueOutEditAuditContext.reasonText ?? null,
              customer_communication_approved: dueOutEditAuditContext.customerCommunicationApproved ?? null,
            }
          : null,
      );
      if (!result.ok) throw new Error(result.error || 'Failed to update ticket details');

      // OCIS-R73-FIX (HIGH-1): ticket-detail edits (type, scheduled_start_time,
      // start_time, due_out, is_comeback) were silent state changes — the
      // timeline had no record of who changed which field. Only emit when
      // a date field actually changed — the DB enum lacks a generic
      // `ticket_edited` value (flagged PRODUCT), and using a mismatched
      // enum corrupts timeline semantics (see R66 regression for
      // logTicketEdited). type/is_comeback changes remain PRODUCT-open.
      try {
        const keys = Object.keys(updates);
        const dateFields = ['scheduled_start_time', 'start_time', 'due_out'];
        const dateChanged = keys.some(k => dateFields.includes(k));
        if (dateChanged) {
          const { data: ticketShop } = await supabase
            .from('tickets')
            .select('shop_id')
            .eq('id', ticketId)
            .maybeSingle();
          if (ticketShop?.shop_id) {
            const { data: { user } } = await supabase.auth.getUser();
            const summary = keys.filter(k => dateFields.includes(k)).join(', ');
            // Phase 5.2 — when a Due Out change was gated, surface the
            // canonical audit payload under `payload.due_out_edit` so
            // existing `ticket_date_changed` consumers continue to work
            // unchanged while the timeline carries old/new/kind/reason/
            // actor for any subsequent review. A new `due_out_changed`
            // event type is deferred to Phase 5.3 alongside the
            // `DUE_OUT_CHANGED` AdminActionType.
            // The SERVER's record of the Due Out change (old value read by the server, kind, reason).
            const dueOutEditAuditPayload = result.dueOutEdit;
            await logTicketEventToOcis({
              ticketId,
              shopId: ticketShop.shop_id,
              eventType: 'ticket_date_changed',
              content: `Ticket schedule changed: ${summary}`,
              authorUserId: user?.id,
              payload: {
                changes: updates,
                via: 'edit_ticket_details_modal',
                ...(dueOutEditAuditPayload
                  ? { due_out_edit: dueOutEditAuditPayload }
                  : {}),
              },
            });
          }
        }
      } catch (ocisErr) {
        console.error('[EditTicketDetailsModal] OCIS log error:', ocisErr);
      }

      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      // Chronomancer R-WAITER-8: cascade after Waiter lock changes. After
      // R-WAITER-2 the Waiter → Drop-off conversion only writes `type`
      // and `scheduled_start_time` — neither is in useLiveReshuffle's
      // pre-R-WAITER-7 field list, and even with R-WAITER-7 the hook
      // runs only on viewers with Dayboard open. Trigger server cascade
      // here so the now-flexible Drop-off immediately reflows relative
      // to the rest of the tech's schedule. Same rationale applies when
      // rebooking a Waiter (scheduled_start_time changed) — the lock
      // anchor moved, downstream tickets need to follow.
      try {
        const shouldCascade =
          Object.prototype.hasOwnProperty.call(updates, 'scheduled_start_time') ||
          Object.prototype.hasOwnProperty.call(updates, 'type');
        if (shouldCascade) {
          const { data: ticketCtx } = await supabase
            .from('tickets')
            .select('shop_id, scheduled_date, assigned_tech_id')
            .eq('id', ticketId)
            .maybeSingle();
          if (ticketCtx?.shop_id && ticketCtx?.scheduled_date) {
            const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
            await triggerBoardCascade(
              ticketCtx.shop_id,
              ticketCtx.scheduled_date,
              ticketCtx.assigned_tech_id ?? undefined,
            );
          }
        }
      } catch (cascadeErr) {
        console.warn('[EditTicketDetailsModal] Cascade after conversion failed (non-fatal):', cascadeErr);
      }

      toast({
        title: 'Ticket Details Updated',
        description: 'Ticket type, times, and comeback status have been updated successfully',
      });

      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update ticket details',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Edit Ticket Details</DialogTitle>
          <DialogDescription>
            Update ticket type, scheduled times, and comeback status
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="ticketType">Ticket Type</Label>
            <Select value={ticketType} onValueChange={(value: 'Waiter' | 'Drop-off') => setTicketType(value)}>
              <SelectTrigger id="ticketType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Waiter">Waiter</SelectItem>
                <SelectItem value="Drop-off">Drop-off</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scheduledStartTime">Scheduled Start Time</Label>
            <Input
              id="scheduledStartTime"
              type="datetime-local"
              value={scheduledStartTime}
              onChange={(e) => setScheduledStartTime(e.target.value)}
              disabled={ticketType === 'Drop-off'}
            />
            <p className="text-xs text-muted-foreground">
              {ticketType === 'Waiter' 
                ? '🔒 This time is locked and cannot be changed by the optimizer' 
                : 'Only available for Waiter appointments'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dueOut">Promised Due Out Time</Label>
            <Input
              id="dueOut"
              type="datetime-local"
              value={dueOut}
              onChange={(e) => setDueOut(e.target.value)}
              disabled={!dueOutAuthorized}
            />
            <p className="text-xs text-muted-foreground">
              Customer promised completion time (must be after predicted finish)
            </p>
            {/* Phase 5.2 — Due Out edit gating UI. Only shown when the
                user is changing Due Out from its current value. Reason
                is required; "Other" requires free-text. */}
            {dueOutWillChange && (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
                {!dueOutAuthorized && (
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    You are not authorized to change Due Out. Service
                    Writer, Manager, Admin, or Owner role required.
                  </p>
                )}
                <Label htmlFor="dueOutReason">Reason for Due Out change</Label>
                <Select
                  value={dueOutReason}
                  onValueChange={(v) => setDueOutReason(v as DueOutEditReason)}
                  disabled={!dueOutAuthorized}
                >
                  <SelectTrigger id="dueOutReason">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {DUE_OUT_EDIT_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dueOutReason === 'other' && (
                  <Input
                    placeholder="Describe the reason"
                    value={dueOutReasonText}
                    onChange={(e) => setDueOutReasonText(e.target.value)}
                    disabled={!dueOutAuthorized}
                  />
                )}
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="dueOutCustomerNotified"
                    checked={dueOutCustomerNotified}
                    onCheckedChange={(checked) =>
                      setDueOutCustomerNotified(checked === true)
                    }
                    disabled={!dueOutAuthorized}
                  />
                  <Label
                    htmlFor="dueOutCustomerNotified"
                    className="text-xs font-normal cursor-pointer"
                  >
                    Customer notified / approved (optional)
                  </Label>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox 
              id="isComeback" 
              checked={isComeback} 
              onCheckedChange={(checked) => setIsComeback(checked === true)} 
            />
            <Label htmlFor="isComeback" className="text-sm font-normal cursor-pointer">
              This is a comeback/warranty ticket 🚨
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
